/**
 * CR229: T6 - Deal Data Inspector
 * 
 * Shows field presence, values, and source paths for a selected deal.
 * Helps designers understand what data exists for template binding.
 */

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Check,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Ban,
  Database,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { FieldDebug, FieldCategory } from "@/lib/deals/fieldResolver";
import { getFieldsByCategory, getFieldSummary } from "@/lib/deals/fieldResolver";

interface DealDataInspectorProps {
  fieldMap: FieldDebug[];
  warnings?: { section: string; missingField: string; message: string }[];
  className?: string;
  defaultOpen?: boolean;
}

const CATEGORY_LABELS: Record<FieldCategory, string> = {
  header: "Header Fields",
  value: "Value Fields",
  dates: "Date Fields",
  urls: "URL Fields",
  fine_print: "Fine Print / Exclusions",
  brands: "Brands / Merchants",
  source_meta: "Source Metadata",
};

const CATEGORY_ORDER: FieldCategory[] = [
  "header",
  "value",
  "dates",
  "urls",
  "fine_print",
  "brands",
  "source_meta",
];

export function DealDataInspector({
  fieldMap,
  warnings = [],
  className,
  defaultOpen = true,
}: DealDataInspectorProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<FieldCategory>>(
    new Set(defaultOpen ? CATEGORY_ORDER : [])
  );
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  
  const groupedFields = getFieldsByCategory(fieldMap);
  const summary = getFieldSummary(fieldMap);
  
  const toggleCategory = (cat: FieldCategory) => {
    const next = new Set(expandedCategories);
    if (next.has(cat)) {
      next.delete(cat);
    } else {
      next.add(cat);
    }
    setExpandedCategories(next);
  };
  
  const toggleField = (key: string) => {
    const next = new Set(expandedFields);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setExpandedFields(next);
  };
  
  return (
    <Card className={cn("", className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Database className="h-4 w-4" />
            Data Inspector
          </CardTitle>
          {/* Summary Badges */}
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] gap-1 bg-emerald-50 text-emerald-700 border-emerald-200">
              <Check className="h-2.5 w-2.5" />
              {summary.present}
            </Badge>
            <Badge variant="outline" className="text-[10px] gap-1 bg-amber-50 text-amber-700 border-amber-200">
              <AlertTriangle className="h-2.5 w-2.5" />
              {summary.missing}
            </Badge>
            <Badge variant="outline" className="text-[10px] gap-1 bg-slate-50 text-slate-600 border-slate-200">
              <Ban className="h-2.5 w-2.5" />
              {summary.null}
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {/* Warnings Section */}
        {warnings.length > 0 && (
          <div className="px-4 pb-3">
            <div className="p-2 rounded bg-amber-50 border border-amber-200 space-y-1">
              <p className="text-xs font-medium text-amber-800 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Template requires missing fields
              </p>
              {warnings.map((w, i) => (
                <p key={i} className="text-[10px] text-amber-700 pl-4">
                  • {w.message}
                </p>
              ))}
            </div>
          </div>
        )}
        
        <ScrollArea className="h-[400px]">
          <div className="space-y-1 p-2">
            {CATEGORY_ORDER.map((category) => {
              const fields = groupedFields[category];
              if (fields.length === 0) return null;
              
              const isExpanded = expandedCategories.has(category);
              const presentCount = fields.filter(f => f.status === "present").length;
              
              return (
                <Collapsible
                  key={category}
                  open={isExpanded}
                  onOpenChange={() => toggleCategory(category)}
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-between h-8 px-2 hover:bg-muted"
                    >
                      <span className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" />
                        )}
                        <span className="text-xs font-medium">{CATEGORY_LABELS[category]}</span>
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {presentCount}/{fields.length}
                      </span>
                    </Button>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="pl-4 pr-2 py-1 space-y-0.5">
                      {fields.map((field) => (
                        <FieldRow
                          key={field.key}
                          field={field}
                          isExpanded={expandedFields.has(field.key)}
                          onToggle={() => toggleField(field.key)}
                        />
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

// ================================================================
// FIELD ROW COMPONENT
// ================================================================

interface FieldRowProps {
  field: FieldDebug;
  isExpanded: boolean;
  onToggle: () => void;
}

function FieldRow({ field, isExpanded, onToggle }: FieldRowProps) {
  const statusIcon = {
    present: <Check className="h-3 w-3 text-emerald-600" />,
    missing: <AlertTriangle className="h-3 w-3 text-amber-500" />,
    null: <Ban className="h-3 w-3 text-slate-400" />,
  }[field.status];
  
  const valuePillBg = {
    present: "bg-emerald-50 border-emerald-200",
    missing: "bg-amber-50 border-amber-200",
    null: "bg-slate-50 border-slate-200",
  }[field.status];
  
  return (
    <div className="border rounded bg-background">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 p-1.5 hover:bg-muted/50 text-left"
      >
        {statusIcon}
        <span className="text-xs flex-1 truncate">{field.label}</span>
        <Info className="h-3 w-3 text-muted-foreground" />
      </button>
      
      {isExpanded && (
        <div className="px-2 pb-2 space-y-1.5 border-t bg-muted/20">
          {/* Value */}
          <div className="pt-1.5">
            <span className="text-[10px] text-muted-foreground">Value:</span>
            <div className={cn("mt-0.5 px-2 py-1 rounded border text-xs font-mono", valuePillBg)}>
              {field.status === "present" ? (
                <span className="break-all">
                  {typeof field.value === "object" 
                    ? JSON.stringify(field.value) 
                    : String(field.value)}
                </span>
              ) : field.status === "null" ? (
                <span className="italic text-slate-500">null</span>
              ) : (
                <span className="italic text-amber-600">missing</span>
              )}
            </div>
          </div>
          
          {/* Source Path */}
          <div>
            <span className="text-[10px] text-muted-foreground">Source Path:</span>
            <code className="block mt-0.5 px-2 py-1 rounded bg-slate-100 text-[10px] font-mono text-slate-700">
              {field.sourcePath}
            </code>
          </div>
          
          {/* Notes */}
          {field.notes && (
            <div className="flex items-center gap-1 text-[10px] text-amber-600">
              <AlertTriangle className="h-3 w-3" />
              {field.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
